import { z } from 'zod';
import { NOTIFICATION_CATEGORIES } from '../../src/domain/notification/notificationCatalog';
import {
  ActivityTypeSchema,
  IdSchema,
  InstantSchema,
  JobOrderNoSchema,
  KitIdSchema,
} from './common.schema';

/**
 * The audit trail. Every mutation writes a row with actor, action, target, type
 * and a UTC timestamp, in the same transaction as the mutation. The table is
 * append only; there is deliberately no update and no delete endpoint.
 * Section 7.10 item 10.
 */
export const ActivityEntrySchema = z.object({
  id: IdSchema,
  byUserId: IdSchema.nullable(),
  byUserName: z.string().nullable(),
  action: z.string(),
  /**
   * What was acted on: a job order number, a person, a setting. A doctor's or
   * patient's name is never written into it. It is stored beside the row and
   * composed onto the target, as `JO-2054 · Dr. Jane Roe`, only for a caller
   * holding seeIdentity; everybody else reads the target with the name absent.
   */
  target: z.string(),
  type: ActivityTypeSchema,
  /** The case this row belongs to, when it belongs to one. */
  jobOrderNo: JobOrderNoSchema.nullable(),
  /** The tray, when the action was about one. */
  kitId: KitIdSchema.nullable(),
  createdAt: InstantSchema,
});

export const NotificationSchema = z.object({
  id: IdSchema,
  /**
   * What happened: `case.assigned`, `review.waiting`. The request of
   * 7 October 2026, section 1, in src/domain/notification. A row written
   * before it carries one of the old six, `alert` to `note`, and is still read.
   */
  type: z.string(),
  /** The group it is filtered by, and what it is called. Read off the type by the server. */
  category: z.enum(NOTIFICATION_CATEGORIES).optional(),
  title: z.string().optional(),
  /** Also for the device to show, to somebody who allowed it there. */
  push: z.boolean().optional(),
  text: z.string(),
  createdAt: InstantSchema,
  read: z.boolean(),
  /** The job order the notification points at, when there is one. */
  target: z.string().nullable(),
});

export type ActivityEntry = z.infer<typeof ActivityEntrySchema>;
export type Notification = z.infer<typeof NotificationSchema>;
